import { useEffect, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { LIMITS, levelRoom, numberedRoom, type RoomId } from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';
import { Button } from '../../ui';
import type { ChatApi } from '../../state/useChat';
import type { PublicMission } from '../../types';

/**
 * Which room, and the ways to another one.
 *
 * The header button is the current room's name in the product's disclosure
 * style, so it reads as a title that happens to open. Underneath, three ways
 * out, in the order they are likely to be wanted: a number typed like a
 * class code, the room for the level you are standing on, and the rooms you
 * were in recently. Nothing here lists every room; there are ninety-nine
 * numbered ones and a hundred level ones, and a list that long is a search
 * box with extra steps.
 *
 * The number field is validated by the protocol's `numberedRoom`, never by
 * a copy of its rule, so the drawer and the Worker cannot disagree about
 * what a room is.
 *
 * The level's room is where a learner is by default, and the header says so
 * in one word rather than leaving them to work out why the room changed under
 * them. Typing a number pins it instead; "This level" is how the level gets
 * the decision back, so it is offered whenever it is not already in charge —
 * including while standing in the level's own room, pinned there by hand.
 *
 * ## Where the people are
 *
 * Every room offered now says how many people are in it, and the fix that is
 * meant to be is the emptiest thing this product had: a learner typed a room
 * number, arrived in silence, said something to nobody, and concluded that the
 * chat was dead. It was not dead — it was in room 1 — and there was no way to
 * know that without visiting all ninety-nine. A number beside each room turns
 * "pick a room" into a decision with information in it.
 *
 * The count is members and not sockets. A room of a hundred tabs that are
 * reading is not a room with a hundred people in it, and saying so would be a
 * dressed-up version of the same lie the empty room told.
 *
 * It is refreshed when the picker opens, and only then. The index is one
 * Durable Object that every room reports into, the answer is cached for twenty
 * seconds at the edge, and a panel that polled it would be spending a request
 * every few seconds on a question nobody is asking with the drawer shut.
 * A room the index has never heard from is absent from the record entirely,
 * which is read as "empty and quiet" — the truthful reading, since a room
 * nobody has ever opened has never reported anything.
 */
export interface RoomPickerProps {
  room: RoomId;
  recent: RoomId[];
  mission: PublicMission | null;
  /** True while the room is the level's own and moves with the learner. */
  following: boolean;
  /** Where everybody is, from GET /api/chat/rooms. */
  occupancy: ChatApi['occupancy'];
  /** Ask for a fresh snapshot. Called when this opens, and nowhere else. */
  onRefresh(): void;
  /** What a room is called on screen; see RoomStrip's own note. */
  label(room: RoomId): string;
  open: boolean;
  onToggle(): void;
  onClose(): void;
  onJoin(room: RoomId): void;
  onFollow(): void;
}

const DAY_MS = 86_400_000;

/**
 * What one room is like right now, in words.
 *
 * Three states, and the third is the one that matters: somebody in it, nobody
 * in it but people were here recently, and nobody in it for days. "Empty" on
 * its own would be discouraging about a room that was busy an hour ago and
 * flattering about one nobody has opened since spring.
 */
function Occupancy({ at }: { at: { here: number; lastTs: number } | undefined }) {
  const s = useStrings().chat;
  if (!at || (at.here === 0 && at.lastTs === 0)) return <span className="chat-occupancy">{s.occupancy.never}</span>;
  if (at.here > 0) return <span className="chat-occupancy">{s.occupancy.here(at.here)}</span>;
  const days = Math.max(0, Math.floor((Date.now() - at.lastTs) / DAY_MS));
  return <span className="chat-occupancy">{s.occupancy.quiet(days)}</span>;
}

export function RoomPicker({
  room,
  recent,
  mission,
  following,
  occupancy,
  onRefresh,
  label,
  open,
  onToggle,
  onClose,
  onJoin,
  onFollow,
}: RoomPickerProps) {
  const s = useStrings().chat;
  const [value, setValue] = useState('');

  // Once, on the way open. Not on every render of an open picker, and not on
  // a timer: see the note above about what this question costs.
  useEffect(() => {
    if (open) onRefresh();
  }, [open, onRefresh]);

  const typed = value.trim() === '' ? null : numberedRoom(Number(value));
  const level = mission ? levelRoom(mission.level) : null;
  const shortcuts = recent.filter((r) => r !== room);

  const go = (target: RoomId) => {
    onJoin(target);
    setValue('');
    onClose();
  };

  const follow = () => {
    onFollow();
    setValue('');
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (typed) go(typed);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape') return;
    // Ours, not the drawer's: one Escape shuts the picker, a second shuts the drawer.
    e.preventDefault();
    onClose();
  };

  return (
    <div className="chat-room">
      <button
        type="button"
        className="disclose chat-room-btn"
        aria-expanded={open}
        aria-controls="chat-rooms"
        title={s.rooms.change}
        onClick={onToggle}
      >
        <span className="disclose-mark" aria-hidden="true" />
        <span className="label">{label(room)}</span>
        {following && (
          <span className="chat-follows" title={s.rooms.followsWhy}>
            {s.rooms.follows}
          </span>
        )}
      </button>

      {open && (
        <div id="chat-rooms" className="chat-pop" onKeyDown={onKeyDown}>
          <form className="chat-room-go" onSubmit={submit}>
            <label className="label" htmlFor="chat-room-n">
              {s.rooms.number}
            </label>
            <input
              id="chat-room-n"
              className="chat-input num"
              type="number"
              inputMode="numeric"
              min={LIMITS.room.min}
              max={LIMITS.room.max}
              step={1}
              placeholder={s.rooms.range(LIMITS.room.min, LIMITS.room.max)}
              autoComplete="off"
              autoFocus
              // A room id is a number the Worker matches, so the field is left
              // to right in both languages and so is the range in its
              // placeholder.
              dir="ltr"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <Button type="submit" variant="quiet" disabled={!typed || typed === room}>
              {s.rooms.go}
            </Button>
            {/* The number typed so far, before it is committed to. This is the
                whole of the empty-room fix: it is the moment somebody is
                deciding, and the moment they can still change their mind. */}
            {typed && <Occupancy at={occupancy[typed]} />}
          </form>

          {level && !following && (
            <div className="chat-room-row">
              <span className="label">{s.rooms.thisLevel}</span>
              <button type="button" className="tag tag-on chat-room-tag" onClick={follow} title={s.rooms.thisLevelWhy}>
                {label(level)}
              </button>
              <Occupancy at={occupancy[level]} />
            </div>
          )}

          {shortcuts.length > 0 && (
            <div className="chat-room-row">
              <span className="label">{s.rooms.recent}</span>
              <div className="chat-room-tags">
                {shortcuts.map((r) => (
                  <span key={r} className="chat-room-shortcut">
                    <button type="button" className="tag chat-room-tag" onClick={() => go(r)}>
                      {label(r)}
                    </button>
                    <Occupancy at={occupancy[r]} />
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RoomPicker;
